import { canCompleteAddEmployees, getSubscriberPageForEnrollment } from 'api/operations/electionService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import { FiltersObject } from 'store/general/paginationDelegate';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import {
  EmployeeInvitationStatusTypes,
  SubscriberAddEmployeesProgress,
  SubscribersFilter,
} from 'types/electionService';
import { enrollmentEmployeeActions } from 'store/pages/enrollment/add-employees/enrollmentEmployeeActions';
import { getStandardActionsWithSelfEnrollFeature } from 'store/modules/add-employees/inviteEmployeeActions';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ChecklistCard, ChecklistCompletionStatus, USER_ACTIONS } from 'types/enrollmentService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { BypassModalDelegate } from './bypassModalDelegate';

interface AddEmployeesDomainParams {
  periodId: string;
  groupId: string;
}

export interface AddEmployeesFilters extends FiltersObject {
  status: EmployeeInvitationStatusTypes | null;
  subProgress: SubscriberAddEmployeesProgress | null;
  employeeClass: string | null;
  employeeName: string;
}

export default class AddEmployeesPageStore extends IAddEmployeesPage<AddEmployeesFilters> {
  protected _domainParams: AddEmployeesDomainParams;
  private _rootStore: RootStore = getRootStoreInstance();
  private _isEnrollmentLoading: boolean = true;
  public enrollmentPeriodModule = new EnrollmentPeriodModule();
  public bypassModalDelegate = new BypassModalDelegate({
    groupId: this._domainParams.groupId,
    periodId: this._domainParams.periodId,
    onRedirect: this.onBack.bind(this),
  });

  public get isEnrollmentLoading(): boolean {
    return this._isEnrollmentLoading;
  }

  public get showCensusBypassFeature(): boolean {
    return this._rootStore.domainStoreV2.showCensusBypassFeature;
  }

  public get stepCanComplete(): boolean {
    return this.employees.length > 0;
  }

  public get isStepCompleted(): boolean {
    if (!this.enrollmentPeriodModule.checklist) {
      return false;
    }

    return (
      this.enrollmentPeriodModule.getStepCompletionStatus('DEFAULT', ChecklistCard.ADD_EMPLOYEES) ===
      ChecklistCompletionStatus.COMPLETED
    );
  }

  public async getEnrollmentPeriod(): Promise<void> {
    this._isEnrollmentLoading = true;
    await this.enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainParams.periodId);
    runInAction(() => {
      this._isEnrollmentLoading = false;
    });
  }

  public async getEmployees(): Promise<void> {
    this._setIsLoading(true);
    const { result, totalElements, totalPages } = await getSubscriberPageForEnrollment({
      groupId: this._domainParams.groupId,
      periodId: this._domainParams.periodId,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      status: this.paginationDelegate.filters.status ? [this.paginationDelegate.filters.status] : [],
      employeeClasses: this.paginationDelegate.filters.employeeClass
        ? [this.paginationDelegate.filters.employeeClass]
        : [],
      progress: this.paginationDelegate.filters.subProgress ? [this.paginationDelegate.filters.subProgress] : [],
      filter: SubscribersFilter.ALL,
      name: this.paginationDelegate.filters.employeeName,
      page: this.paginationDelegate.page,
    });
    runInAction(() => {
      this._createEmployeeDelegates(result);
      this.paginationDelegate.setTotalElements(totalElements);
      this.paginationDelegate.setTotalPages(totalPages);
      this._setIsLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    this._setIsLoading(true);
    const canComplete = await canCompleteAddEmployees(this._domainParams.groupId, this._domainParams.periodId);

    if (canComplete) {
      await requestApplyUserAction(this._domainParams.periodId, USER_ACTIONS.COMPLETE_ADD_EMPLOYEES);
      runInAction(() => {
        this._setIsLoading(false);
        this.onBack();
      });
    } else {
      this._setIsLoading(false);
      toastManager.addToast('Some employees have incomplete data! You cannot proceed now.', ToastColorTypes.WARNING);
    }
  }

  public async onUncompleteStep(): Promise<void> {
    this._setIsLoading(true);
    await requestApplyUserAction(this._domainParams.periodId, USER_ACTIONS.UNCOMPLETE_ADD_EMPLOYEES);
    await runInAction(async () => {
      await this.enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainParams.periodId);
      this._setIsLoading(false);
    });
  }

  public onBack(): void {
    bpHistory.push(`/checklist/${this._domainParams.periodId}`);
  }

  constructor(params: AddEmployeesDomainParams) {
    super(
      { status: null, employeeName: '', subProgress: null, employeeClass: null },
      getStandardActionsWithSelfEnrollFeature(enrollmentEmployeeActions),
      params,
    );
    this._domainParams = params;

    makeObservable<AddEmployeesPageStore, '_domainParams' | '_isEnrollmentLoading'>(this, {
      _domainParams: observable,
      _isEnrollmentLoading: observable,
      bypassModalDelegate: observable,
      stepCanComplete: computed,
      isEnrollmentLoading: computed,
      isStepCompleted: computed,
      showCensusBypassFeature: computed,
      getEnrollmentPeriod: action.bound,
      getEmployees: action.bound,
      onComplete: action.bound,
      onUncompleteStep: action.bound,
      onBack: action.bound,
    });
  }
}

const AddEmployeesPageStoreContext = createContext(new AddEmployeesPageStore({} as AddEmployeesDomainParams));

export const useAddEmployeesPageStore = (): AddEmployeesPageStore => {
  return useContext(AddEmployeesPageStoreContext);
};

export { AddEmployeesPageStore, AddEmployeesPageStoreContext };
